import React, { useState, useEffect } from 'react';
import { monthlyOverviewAPI, adminAPI, timeOffAPI } from '../../services/api';
import { startOfISOWeek, addWeeks } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import './AdminMonthlyOverview.css';
import { WorkLogEditModal } from './WorkLogEditModal';
import { getRoleDisplayName } from '../../utils/roleTranslations';
import { Crown, User, Mail, Clock, Calendar, Edit, Repeat, BarChart3, Briefcase, Palmtree, Heart, Gift,
  Home, Info, Plus, Trash2, Circle, SmartphoneNfc, Bot, Clipboard, X,
  ArrowLeft, ArrowRight, Wrench, Pencil, Laptop, Target,
 } from 'lucide-react';

interface DayData {
  date: string;
  dayOfWeek: number;
  isWorkday: boolean;
  isHoliday: boolean;
  isToday: boolean;
  workLogs: Array<{
    id: number;
    startTime: string;
    endTime: string | null;
    totalMinutes: number | null;
    description: string | null;
    logMethod: string;
    logMethod2?: string;
  }>;
  timeOff: {
    type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
    reason: string | null;
  } | null;
  timeOffEntries?: Array<{
    id: number;
    type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
    reason: string | null;
    startDate: string;
    endDate: string;
  }>;
  holiday: {
    name: string;
    description: string | null;
  } | null;
  actualMinutes: number;
  expectedMinutes: number;
  status: 'workday' | 'weekend' | 'holiday' | 'vacation' | 'sick' | 'current' | 'overtime';
}

interface MonthlyData {
  period: {
    startDate: string;
    endDate: string;
    type: 'week' | 'month';
    year: number;
    month: number;
    week: number | null;
  };
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    weeklyHours: number;
    workDays: string;
    dailyExpectedMinutes: number;
    startDate: string;
    initialOvertimeMinutes?: number; // Neu hinzugefügt
  };
  summary: {
    totalActualMinutes: number;
    totalExpectedMinutes: number;
    overtimeMinutes: number;
    totalActualMinutesUpToToday: number;
    totalExpectedMinutesUpToToday: number;
    overtimeMinutesUpToToday: number;
    isCurrentPeriod: boolean;
    workdayMinutes: number;
    overtimeFromNonWorkdays: number;
    holidayMinutes: number;
    vacationMinutes: number;
    sickMinutes: number;
    totalDays: number;
    workdays: number;
    holidays: number;
    vacations: number;
    sickDays: number;
    cumulativeOvertimeFromPrevious: number;
    totalCumulativeOvertime: number;
  };
  days: DayData[];
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
}

interface AdminMonthlyData extends MonthlyData {
  permissions: {
    canEdit: boolean;
    canViewOthers: boolean;
    role: string;
  };
}

interface VacationSummary {
  totalVacationDays: number;
  usedVacationDays: number;
  pendingVacationDays: number;
  remainingVacationDays: number;
  year: number;
  user: {
    firstName: string;
    lastName: string;
    weeklyHours: number;
    workDays: string;
  };
}

interface TimeOffEntry {
  id: number;
  startDate: string;
  endDate: string;
  type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason: string | null;
  workingDays: number;
  createdAt: string;
}

export const AdminMonthlyOverview: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [viewType, setViewType] = useState<'month' | 'week'>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AdminMonthlyData | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<{
    availableMonths: Array<{ year: number; month: number; label: string }>;
    availableYears: number[];
    userStartDate: string;
  } | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  const [editingWorkLog, setEditingWorkLog] = useState<any>(null);
  const [editingDate, setEditingDate] = useState<string>('');

  const [vacationSummary, setVacationSummary] = useState<VacationSummary | null>(null);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOffEntry[]>([]);
  const [showVacationForm, setShowVacationForm] = useState(false);
  const [showSickForm, setShowSickForm] = useState(false);
  const [vacationForm, setVacationForm] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    type: 'VACATION' as 'VACATION' | 'SPECIAL_LEAVE'
  });
  const [sickForm, setSickForm] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    doctorNote: false
  });
  const [calculatedWorkingDays, setCalculatedWorkingDays] = useState<number>(0);
  const [conflictDates, setConflictDates] = useState<string[]>([]);
  
  useEffect(() => {
    loadUsers();
    loadAvailablePeriods();
  }, []);

  useEffect(() => {
    if (availablePeriods && selectedUserId) {
      loadData();
    }
  }, [viewType, selectedYear, selectedMonth, selectedWeek, selectedUserId, availablePeriods]);

  useEffect(() => {
    if (selectedUserId) {
      loadVacationSummary();
      loadTimeOffEntries();
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (vacationForm.startDate && vacationForm.endDate && selectedUserId && data) {
      calculateWorkingDaysForVacation();
      checkVacationConflicts();
    } else {
      setCalculatedWorkingDays(0);
      setConflictDates([]);
    }
  }, [vacationForm.startDate, vacationForm.endDate, selectedUserId, data]);

  const getCurrentWeekNumber = () => {
    const now = new Date();
    const thursday = new Date(now.getTime() + (3 - ((now.getDay() + 6) % 7)) * 86400000);
    const year = thursday.getFullYear();
    const firstThursday = new Date(year, 0, 4);
    const weekNum = 1 + Math.round(((thursday.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
    
    return Math.max(1, Math.min(weekNum, getWeeksInYear(year)));
  };

  useEffect(() => {
    if (viewType === 'week') {
      const currentWeek = getCurrentWeekNumber();
      const currentYear = new Date().getFullYear();
      
      setSelectedYear(currentYear);
      setSelectedWeek(currentWeek);
    }
  }, [viewType]);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers({ limit: 1000 });
      const activeUsers = response.data.users.filter((u: User) => u.isActive);
      setUsers(activeUsers);
      
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Benutzer' });
    }
  };

  const loadAvailablePeriods = async () => {
    try {
      const response = await monthlyOverviewAPI.getAvailablePeriods();
      setAvailablePeriods(response.data);
      
      if (viewType === 'week') {
        const currentWeek = getCurrentWeekNumber();
        setSelectedWeek(currentWeek);
      }
    } catch (error) {
      console.error('Error loading available periods:', error);
    }
  };

  const calculateWeekDates = (year: number, week: number) => {
    const firstWeek = startOfISOWeek(new Date(year, 0, 4)); // 4. Januar ist immer in KW 1
    const targetWeek = addWeeks(firstWeek, week - 1);
    
    const startDate = startOfISOWeek(targetWeek);
    startDate.setDate(startDate.getDate() +1); //Damit Montag der erste Tag der Woche ist
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    return { startDate, endDate };
  };

  const loadData = async () => {
    if (!selectedUserId) return;
    
    setLoading(true);
    try {
      let params: any = {
        targetUserId: selectedUserId?.toString()
      };
      
      if (viewType === 'week') {
        const { startDate, endDate } = calculateWeekDates(selectedYear, selectedWeek);
        
        params = {
          ...params,
          year: selectedYear,
          week: selectedWeek,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        };
      } else {
        params = {
          ...params,
          year: selectedYear,
          month: selectedMonth
        };
      }

      console.log('🔄 Loading admin monthly overview with params:', params);
      
      const response = await monthlyOverviewAPI.getAdminMonthlyOverview(params);
      
      console.log('Admin monthly overview response:', response.data);
      
      setData(response.data);
      setMessage({ type: '', text: '' });
    } catch (error: any) {
      console.error('Error loading admin monthly overview:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Laden der Monatsübersicht' 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVacationSummary = async () => {
    if (!selectedUserId) return;
    
    try {
      const response = await timeOffAPI.getVacationSummary({ targetUserId: selectedUserId });
      setVacationSummary(response.data);
    } catch (error) {
      console.error('Error loading vacation summary:', error);
    }
  };

  const loadTimeOffEntries = async () => {
    if (!selectedUserId) return;
    
    try {
      const response = await timeOffAPI.getAllTimeOffRequestsAdmin({
        targetUserId: selectedUserId.toString(),
        limit: 100
      });
      setTimeOffEntries(response.data.timeOffs);
    } catch (error) {
      console.error('Error loading time off entries:', error);
    }
  };

  const calculateWorkingDaysForVacation = () => {
    if (!data || !vacationForm.startDate || !vacationForm.endDate) return;

    const userWorkDays = data.user.workDays ? 
      data.user.workDays.split(',').map(d => parseInt(d)) : 
      [1, 2, 3, 4, 5];

    const startDate = new Date(vacationForm.startDate);
    const endDate = new Date(vacationForm.endDate);
    let workingDays = 0;
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      const dateStr = currentDate.toISOString().split('T')[0];
      
      if (userWorkDays.includes(normalizedDay)) {
        const isHoliday = data.days.find(day => 
          day.date === dateStr && day.holiday
        );
        
        if (!isHoliday) {
          workingDays++;
        }
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setCalculatedWorkingDays(workingDays);
  };

  const checkVacationConflicts = () => {
    if (!data || !vacationForm.startDate || !vacationForm.endDate) return;

    const conflicts: string[] = [];
    const startDate = new Date(vacationForm.startDate);
    const endDate = new Date(vacationForm.endDate);
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const dayData = data.days.find(day => day.date === dateStr);
      
      if (dayData && dayData.timeOff && dayData.timeOff.type === 'VACATION') {
        conflicts.push(dateStr);
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setConflictDates(conflicts);
  };

  const handleCreateVacation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (conflictDates.length > 0) {
      setMessage({
        type: 'error',
        text: `Konflikt: ${conflictDates.length} Tag(e) sind bereits als Urlaub genehmigt!`
      });
      return;
    }

    if (vacationSummary && calculatedWorkingDays > vacationSummary.remainingVacationDays && vacationForm.type === 'VACATION') {
      setMessage({
        type: 'error',
        text: `Nicht genügend Urlaubstage verfügbar. Benötigt: ${calculatedWorkingDays}, Verfügbar: ${vacationSummary.remainingVacationDays}`
      });
      return;
    }

    try {
      setLoading(true);
      await timeOffAPI.createVacationRetroactive({
        userId: selectedUserId!,
        startDate: vacationForm.startDate,
        endDate: vacationForm.endDate,
        reason: vacationForm.reason || undefined,
        type: vacationForm.type
      });

      const typeText = vacationForm.type === 'SPECIAL_LEAVE' ? 'Sonderurlaub' : 'Urlaub';
      setMessage({ type: 'success', text: `${typeText} erfolgreich eingetragen` });
      
      setVacationForm({
        startDate: '',
        endDate: '',
        reason: '',
        type: 'VACATION'
      });
      setShowVacationForm(false);
      
      loadData();
      loadVacationSummary();
      loadTimeOffEntries();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Eintragen des Urlaubs'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSickLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await timeOffAPI.createSickLeave({
        userId: selectedUserId!,
        startDate: sickForm.startDate,
        endDate: sickForm.endDate,
        reason: sickForm.reason || undefined,
        doctorNote: sickForm.doctorNote
      });

      setMessage({ type: 'success', text: 'Krankmeldung erfolgreich eingetragen' });
      
      setSickForm({
        startDate: '',
        endDate: '',
        reason: '',
        doctorNote: false
      });
      setShowSickForm(false);
      
      loadData();
      loadTimeOffEntries();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Eintragen der Krankmeldung'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeOff = async (timeOffId: number, type: string) => {
    const typeText = type === 'VACATION' ? 'Urlaubsantrag' : 'Krankmeldung';
    
    if (!window.confirm(`Sind Sie sicher, dass Sie den ${typeText} löschen möchten?`)) {
      return;
    }

    try {
      setLoading(true);
      await timeOffAPI.deleteTimeOffRequest(timeOffId);
      
      setMessage({ 
        type: 'success', 
        text: `${typeText} erfolgreich gelöscht` 
      });
      
      if (!selectedUserId) return;
      
      let params: any = {
        targetUserId: selectedUserId?.toString()
      };
      
      if (viewType === 'week') {
        const { startDate, endDate } = calculateWeekDates(selectedYear, selectedWeek);
        params = {
          ...params,
          year: selectedYear,
          week: selectedWeek,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        };
      } else {
        params = {
          ...params,
          year: selectedYear,
          month: selectedMonth
        };
      }

      const response = await monthlyOverviewAPI.getAdminMonthlyOverview(params);
      setData(response.data);
      
      if (showDayModal && selectedDay) {
        const updatedDay = response.data.days.find((day: DayData) => day.date === selectedDay.date);
        if (updatedDay) {
          setSelectedDay(updatedDay);
        }
      }
      
      loadVacationSummary();
      loadTimeOffEntries();
      
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || `Fehler beim Löschen des ${typeText}s`
      });
    } finally {
      setLoading(false);
    }
  };

  const getTimeOffEntriesForDay = (date: string): TimeOffEntry[] => {
    return timeOffEntries.filter(entry => {
      const entryStart = new Date(entry.startDate);
      const entryEnd = new Date(entry.endDate);
      const dayDate = new Date(date);
      
      return dayDate >= entryStart && dayDate <= entryEnd && entry.status === 'APPROVED';
    });
  };

  const getTimeOffIcon = (type: string) => {
    switch (type) {
      case 'SICK': return <Heart size={14}/>;
      case 'VACATION': return <Palmtree size={14}/>;
      case 'SPECIAL_LEAVE': return <Gift size={14}/>;
      default: return <Clipboard size={14}/>;
    }
  };

  const getTimeOffLabel = (type: string, reason?: string | null) => {
    const defaultLabels = {
      'SICK': 'Krank',
      'VACATION': 'Urlaub',
      'SPECIAL_LEAVE': 'Sonderurlaub'
    };
    return reason || defaultLabels[type as keyof typeof defaultLabels] || type;
  };

  const formatMinutes = (minutes: number) => {
    if (minutes === 0) return '0:00';
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const getDayStatusClass = (day: DayData) => {
    const baseClass = 'calendar-day';
    
    if (day.isToday) return `${baseClass} current-day`;
    if (day.status === 'sick') return `${baseClass} sick-day`;
    if (day.status === 'vacation') return `${baseClass} vacation-day`;
    if (day.status === 'holiday') return `${baseClass} holiday-day`;
    if (day.status === 'workday') return `${baseClass} workday`;
    if (day.status === 'weekend') return `${baseClass} weekend-day`;
    if (day.status === 'overtime') return `${baseClass} overtime-day`;
    
    return baseClass;
  };

  const getDayLabel = (day: DayData) => {
    const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    return dayNames[day.dayOfWeek - 1]; // Ziehe 1 ab, weil das Backend 1-7 verwendet, das Array aber 0-6
  };

  const getWeeksInYear = (year: number) => {
    // ISO 8601 Wochen Berechnung
    const jan1 = new Date(year, 0, 1);
    const dec31 = new Date(year, 11, 31);
    
    // Berechne die Wochennummer für den 31. Dezember um die Gesamtzahl zu ermitteln
    const dec31WeekDay = dec31.getDay();
    const dec31DaysFromMonday = dec31WeekDay === 0 ? 6 : dec31WeekDay - 1;
    const dec31Monday = new Date(dec31);
    dec31Monday.setDate(dec31.getDate() - dec31DaysFromMonday);
    
    // Wenn der Montag vom 31. Dezember im nächsten Jahr liegt, hat dieses Jahr 52 Wochen
    if (dec31Monday.getFullYear() > year) {
      return 52;
    }
    
    // Andernfalls basierend auf dem 1. Januar berechnen
    const jan1WeekDay = jan1.getDay();
    
    // Wenn der 1. Januar ein Donnerstag, Freitag, Samstag oder Sonntag ist, beginnt Woche 1 im vorherigen Jahr
    if (jan1WeekDay >= 4 || jan1WeekDay === 0) {
      return 53;
    }
    
    return 52;
  };

  const getWeekDateRange = (year: number, week: number) => {
    const { startDate, endDate } = calculateWeekDates(year, week);
    const adjustedStartDate = new Date(startDate);
    adjustedStartDate.setDate(startDate.getDate()-1);
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setDate(endDate.getDate()-1);

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    };
    return `${formatDate(adjustedStartDate)}-${formatDate(adjustedEndDate)}`;
  };

  const getAvailableMonthsForYear = () => {
    if (!availablePeriods) return [];
    return availablePeriods.availableMonths.filter(m => m.year === selectedYear);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (!availablePeriods) return;
    
    const currentMonthIndex = availablePeriods.availableMonths.findIndex(
      m => m.year === selectedYear && m.month === selectedMonth
    );
    
    if (currentMonthIndex === -1) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = Math.max(0, currentMonthIndex - 1);
    } else {
      newIndex = Math.min(availablePeriods.availableMonths.length - 1, currentMonthIndex + 1);
    }
    
    const newMonth = availablePeriods.availableMonths[newIndex];
    setSelectedYear(newMonth.year);
    setSelectedMonth(newMonth.month);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const weeksInCurrentYear = getWeeksInYear(selectedYear);
    
    if (direction === 'prev') {
      if (selectedWeek > 1) {
        setSelectedWeek(selectedWeek - 1);
      } else {
        // Zum vorherigen Jahr, letzte Woche wechseln
        const prevYear = selectedYear - 1;
        if (availablePeriods?.availableYears.includes(prevYear)) {
          setSelectedYear(prevYear);
          setSelectedWeek(getWeeksInYear(prevYear));
        }
      }
    } else {
      if (selectedWeek < weeksInCurrentYear) {
        setSelectedWeek(selectedWeek + 1);
      } else {
        // Zum nächsten Jahr, erste Woche wechseln
        const nextYear = selectedYear + 1;
        if (availablePeriods?.availableYears.includes(nextYear)) {
          setSelectedYear(nextYear);
          setSelectedWeek(1);
        }
      }
    }
  };

  const openDayModal = (day: DayData) => {
    setSelectedDay(day);
    setShowDayModal(true);
  };

  const closeDayModal = () => {
    setShowDayModal(false);
    setSelectedDay(null);
  };

  const canEditTimes = () => {
    return data?.permissions?.canEdit && currentUser?.role && ['ADMIN', 'MANAGER'].includes(currentUser.role);
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId ? parseInt(userId) : null);
    setData(null);
  };

  const handleEditWorkLog = (workLogId: number) => {
    if (!canEditTimes()) return;

    const workLog = data?.days
      .flatMap(day => day.workLogs)
      .find(log => log.id === workLogId);
    
    if (workLog && selectedDay) {
      setEditingWorkLog(workLog);
      setEditingDate(selectedDay.date);
      setShowWorkLogModal(true);
    }
  };

  const handleAddWorkLog = (date: string) => {
    if (!canEditTimes()) return;
    
    setEditingWorkLog(null);
    setEditingDate(date);
    setShowWorkLogModal(true);
  };

  const handleWorkLogSuccess = async () => {
    try {
      const params: any = {
        targetUserId: selectedUserId
      };
      
      if (viewType === 'week') {
        const { startDate, endDate } = calculateWeekDates(selectedYear, selectedWeek);
        params.year = selectedYear;
        params.week = selectedWeek;
        params.startDate = startDate.toISOString().split('T')[0];
        params.endDate = endDate.toISOString().split('T')[0];
      } else {
        params.year = selectedYear;
        params.month = selectedMonth;
      }

      console.log('🔄 Reloading data after WorkLog change with params:', params);
      
      const response = await monthlyOverviewAPI.getAdminMonthlyOverview(params);
      setData(response.data);
      
      if (showDayModal && selectedDay) {
        const updatedDay = response.data.days.find((day: DayData) => day.date === selectedDay.date);
        if (updatedDay) {
          console.log('Updating selected day with fresh data:', updatedDay);
          setSelectedDay(updatedDay);
        }
      }
      
      setMessage({ type: 'success', text: 'Arbeitszeit erfolgreich gespeichert. Daten werden aktualisiert...' });
      
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error reloading data after WorkLog change:', error);
      setMessage({ type: 'error', text: 'Fehler beim Aktualisieren der Daten' });
    }
  };

  const closeWorkLogModal = () => {
    setShowWorkLogModal(false);
    setEditingWorkLog(null);
    setEditingDate('');
  };

  const formatWorkDays = (workDays: string) => {
    if (!workDays) return 'Mo-Fr';
    
    const dayMapping: { [key: string]: string } = {
      '1': 'Mo',
      '2': 'Di', 
      '3': 'Mi',
      '4': 'Do',
      '5': 'Fr',
      '6': 'Sa',
      '7': 'So'
    };
    
    const days = workDays.split(',').map(day => dayMapping[day.trim()]).filter(Boolean);
    return days.join(', ');
  };

  // Helper function to calculate actual break time from database
  const getActualBreakMinutes = (workLog: any) => {
    // Use breakMinutes from database if available, otherwise return 0
    return workLog.breakMinutes || 0;
  };

  if (loading && !data) {
    return <div className="loading">Lade Admin Monatsübersicht...</div>;
  }

  return (
    <div className="admin-monthly-overview">
      <div className="overview-header">
        <h2><Crown size={24} /> Admin Monatsübersicht</h2>
        
        <div className="admin-controls">
          {/* User Selector */}
          <div className="selector-group user-selector">
            <label>Mitarbeiter:</label>
            <select
              value={selectedUserId || ''}
              onChange={(e) => handleUserChange(e.target.value)}
              className="user-select"
            >
              <option value="">Mitarbeiter auswählen...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({getRoleDisplayName(user.role)}) - {user.email}
                </option>
              ))}
            </select>
          </div>

          {/* View Type Selector */}
          <div className="view-selector">
            <button className={`view-btn ${viewType === 'month' ? 'active' : ''}`} onClick={() => setViewType('month')}>
              <Calendar size={16} /> Monat
            </button>
            <button
              className={`view-btn ${viewType === 'week' ? 'active' : ''}`}
              onClick={() => {
                setViewType('week');
              }}
            >
              <BarChart3 size={16} /> Woche
            </button>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button 
            className="message-close" 
            onClick={() => setMessage({ type: '', text: '' })}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Selected User Info */}
      {data && (
        <div className="selected-user-info">
          <div className="user-details">
            <h3>
              <User size={16} /> {data.user.firstName} {data.user.lastName}
            </h3>
            <div className="user-meta">
              <span><Mail size={16} /> {data.user.email}</span>
              <span><Clock size={16} /> {data.user.weeklyHours}h/Woche</span>
              <span><Calendar size={16} /> Arbeitstage: {formatWorkDays(data.user.workDays)}</span>
              {canEditTimes() && (
                <span className="edit-indicator"><Edit size={16} /> Bearbeitungsmodus aktiv</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Period Selector */}
      <div className="period-selector">
        <div className="selector-group">
          <label>Jahr:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availablePeriods?.availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {viewType === 'month' ? (
          <div className="selector-group">
            <label>Monat:</label>
            <div className="selector-with-navigation">
              <button 
                className="nav-arrow" 
                onClick={() => navigateMonth('prev')}
                disabled={!availablePeriods || availablePeriods.availableMonths.findIndex(m => m.year === selectedYear && m.month === selectedMonth) === 0}
                title="Vorheriger Monat"
              >
                <ArrowLeft size={16} />
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {getAvailableMonthsForYear().map(month => (
                  <option key={`${month.year}-${month.month}`} value={month.month}>
                    {month.label.split(' ')[0]}
                  </option>
                ))}
              </select>
              <button 
                className="nav-arrow" 
                onClick={() => navigateMonth('next')}
                disabled={!availablePeriods || availablePeriods.availableMonths.findIndex(m => m.year === selectedYear && m.month === selectedMonth) === availablePeriods.availableMonths.length - 1}
                title="Nächster Monat"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="selector-group">
            <label>Woche:</label>
            <div className="selector-with-navigation">
              <button 
                className="nav-arrow" 
                onClick={() => navigateWeek('prev')}
                disabled={!availablePeriods || (selectedWeek === 1 && !availablePeriods.availableYears.includes(selectedYear - 1))}
                title="Vorherige Woche"
              >
                <ArrowLeft size={16} />
              </button>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
              >
                {Array.from({ length: getWeeksInYear(selectedYear) }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    KW {i + 1} ({getWeekDateRange(selectedYear, i + 1)})
                  </option>
                ))}
              </select>
              <button 
                className="nav-arrow" 
                onClick={() => navigateWeek('next')}
                disabled={!availablePeriods || (selectedWeek === getWeeksInYear(selectedYear) && !availablePeriods.availableYears.includes(selectedYear + 1))}
                title="Nächste Woche"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        <button className="btn secondary" onClick={loadData} disabled={loading}>
          {loading ? 'Lädt...' : <><Repeat size={16} /> Aktualisieren</>}
        </button>
      </div>

      {data && (
        <>
          {/* Summary Cards */}
          <div className="summary-section">
            <div className="summary-cards">
              <div className="summary-card total">
                <h4>Gesamtzeit</h4>
                <p className="big-number">{formatMinutes(data.summary.totalActualMinutes)}</p>
                <small>Ist-Zeit (gesamt)</small>
              </div>
              <div className="summary-card expected">
                <h4>Soll-Zeit (Periode)</h4>
                <p className="big-number">{formatMinutes(data.summary.totalExpectedMinutes)}</p>
                <small>Erwartet (gesamt)</small>
              </div>
              <div className={`summary-card ${data.summary.overtimeMinutes >= 0 ? 'positive' : 'negative'}`}>
                <h4>Differenz (Periode)</h4>
                <p className="big-number">{formatMinutes(data.summary.overtimeMinutes)}</p>
                <small>{data.summary.overtimeMinutes >= 0 ? 'Überstunden' : 'Unterstunden'} (gesamt)</small>
              </div>
              {data.summary.isCurrentPeriod && (
                <>
                  <div className="summary-card expected">
                    <h4>Soll-Zeit bis heute</h4>
                    <p className="big-number">{formatMinutes(data.summary.totalExpectedMinutesUpToToday)}</p>
                    <small>Erwartet bis {new Date().toLocaleDateString('de-DE')}</small>
                  </div>
                  <div className={`summary-card ${data.summary.overtimeMinutesUpToToday >= 0 ? 'positive' : 'negative'}`}>
                    <h4>Differenz bis heute</h4>
                    <p className="big-number">{formatMinutes(data.summary.overtimeMinutesUpToToday)}</p>
                    <small>{data.summary.overtimeMinutesUpToToday >= 0 ? 'Überstunden' : 'Unterstunden'} bis heute</small>
                  </div>
                </>
              )}
              <div className={`summary-card ${data.summary.cumulativeOvertimeFromPrevious >= 0 ? 'positive' : 'negative'}`}>
                <h4>Saldo Vorperioden</h4>
                <p className="big-number">{formatMinutes(data.summary.cumulativeOvertimeFromPrevious)}</p>
                <small>Übertrag seit {new Date(data.user.startDate).toLocaleDateString('de-DE')}</small>
              </div>
              <div className={`summary-card ${data.summary.totalCumulativeOvertime >= 0 ? 'positive' : 'negative'}`}>
                <h4>Gesamtsaldo</h4>
                <p className="big-number">{formatMinutes(data.summary.totalCumulativeOvertime)}</p>
                <small>
                  {data.summary.cumulativeOvertimeFromPrevious !== 0 && (
                    <>
                      {formatMinutes(data.summary.cumulativeOvertimeFromPrevious)} + {formatMinutes(data.summary.overtimeMinutes)}
                      <br />
                    </>
                  )}
                  Kumuliert seit {new Date(data.user.startDate).toLocaleDateString('de-DE')}
                </small>
              </div>
            </div>

            {/* Day Statistics */}
            <div className="breakdown-cards">
              <div className="breakdown-card">
                <h5><BarChart3 /> Gesamt Tage</h5>
                <span>{data.summary.totalDays}</span>
              </div>
              <div className="breakdown-card">
                <h5><Briefcase /> Arbeitstage</h5>
                <span>{data.summary.workdays}</span>
              </div>
              <div className="breakdown-card">
                <h5><Palmtree /> Urlaubstage</h5>
                <span>{data.summary.vacations}</span>
              </div>
              <div className="breakdown-card">
                <h5><Heart /> Kranktage</h5>
                <span>{data.summary.sickDays}</span>
              </div>
              <div className="breakdown-card">
                <h5><Gift /> Feiertage</h5>
                <span>{data.summary.holidays}</span>
              </div>
            </div>

            {/* Detailed breakdown */}
            <div className="breakdown-cards">
              <div className="breakdown-card">
                <h5>Reguläre Arbeitszeit</h5>
                <span>{formatMinutes(data.summary.workdayMinutes)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Gesamt-Überstunden (nicht reguläre Zeit)</h5>
                <span>{formatMinutes(data.summary.overtimeFromNonWorkdays)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Urlaub</h5>
                <span>{formatMinutes(data.summary.vacationMinutes)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Kranktage</h5>
                <span>{formatMinutes(data.summary.sickMinutes)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Feiertage</h5>
                <span>{formatMinutes(data.summary.holidayMinutes)}</span>
              </div>
            </div>
          </div>

          {/* Calendar with Edit Capabilities */}
          <div className="calendar-section">
            <div className={`calendar ${viewType} ${canEditTimes() ? 'editable' : ''}`}>
              {/* Header for both month and week view */}
              <div className="calendar-header">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                  <div key={day} className="calendar-header-day">{day}</div>
                ))}
              </div>
              
              <div className="calendar-grid">
                {viewType === 'month' ? (
                  (() => {
                    if (!data) return null;
                    
                    const firstDayOfMonth = new Date(data.period.year, data.period.month - 1, 1);
                    const lastDayOfMonth = new Date(data.period.year, data.period.month, 0);
                    const firstDayWeekday = firstDayOfMonth.getDay();
                    // Konvertiere Sonntag=0 zu Sonntag=7, dann berechne den Offset ab Montag
                    const normalizedFirstDay = firstDayWeekday === 0 ? 7 : firstDayWeekday;
                    const mondayOffset = normalizedFirstDay - 1; // Monday=1 should have offset 0
                    
                    const calendarCells = [];
                    
                    for (let i = 0; i < mondayOffset; i++) {
                      calendarCells.push(
                        <div key={`empty-${i}`} className="calendar-day empty-day">
                        </div>
                      );
                    }
                    
                    for (let dayNum = 1; dayNum <= lastDayOfMonth.getDate(); dayNum++) {
                      const year = data.period.year;
                      const month = data.period.month;
                      const dateStr = `${year}-${month.toString().padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`;
                      const dayData = data.days.find(d => d.date === dateStr);
                      
                      if (dayData) {
                        calendarCells.push(
                          <div 
                            key={dayData.date} 
                            className={`${getDayStatusClass(dayData)} clickable`}
                            onClick={() => openDayModal(dayData)}
                            title="Klicken für Details"
                          >

                            <div className="day-header">
                              <span className="day-number">{dayNum}</span>
                              <span className="day-name">{getDayLabel(dayData)}</span>
                            </div>
                            
                            <div className="day-content">
                              {dayData.actualMinutes > 0 && (
                                <div className="work-time">
                                  {formatMinutes(dayData.actualMinutes)}
                                </div>
                              )}
                              
                              {/* NEW: Show daily overtime/undertime */}
                              {dayData.expectedMinutes > 0 && (dayData.actualMinutes !== dayData.expectedMinutes) && (
                                <div className={`day-overtime ${dayData.actualMinutes - dayData.expectedMinutes >= 0 ? 'positive' : 'negative'}`}>
                                  {dayData.actualMinutes - dayData.expectedMinutes >= 0 ? '+' : ''}{formatMinutes(dayData.actualMinutes - dayData.expectedMinutes)}
                                </div>
                              )}
                              
                              {dayData.holiday && (
                                <div className="day-info holiday">
                                  <Gift size={14} /> {dayData.holiday.name}
                                </div>
                              )}
                              
                              {(() => {
                                const dayTimeOffEntries = getTimeOffEntriesForDay(dayData.date);
                                if (dayTimeOffEntries.length === 0 && dayData.timeOff) {
                                  return (
                                    <div className={`day-info timeoff ${dayData.timeOff.type.toLowerCase()}`}>
                                      {getTimeOffIcon(dayData.timeOff.type)} {getTimeOffLabel(dayData.timeOff.type, dayData.timeOff.reason)}
                                    </div>
                                  );
                                }

                                return dayTimeOffEntries.map((entry, index) => (
                                  <div key={`${entry.id}-${index}`} className={`day-info timeoff ${entry.type.toLowerCase()}`}>
                                    {getTimeOffIcon(entry.type)} {getTimeOffLabel(entry.type, entry.reason)}
                                  </div>
                                ));
                              })()}
                              
                              {dayData.workLogs && dayData.workLogs.length > 0 && (
                                <div className="work-sessions">
                                  {dayData.workLogs.map(log => {
                                    // Use actual break minutes from database instead of calculating difference
                                    const actualBreakMinutes = getActualBreakMinutes(log);
                                    const isAutoClockOut = log.description && log.description.includes('Auto-Ausstempeln');
                                    
                                    return (
                                      <div key={log.id} className="work-session">
                                        <small>
                                          {new Date(log.startTime).toLocaleTimeString('de-DE', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                          })}
                                          {log.endTime && 
                                            ` - ${new Date(log.endTime).toLocaleTimeString('de-DE', { 
                                              hour: '2-digit', 
                                              minute: '2-digit' 
                                            })}`
                                          } Uhr
                                          {isAutoClockOut && (
                                            <span className="auto-clockout-indicator" title="Automatisch ausgestempelt">
                                              <Bot size={12} />
                                            </span>
                                          )}
                                        </small>
                                        {log.endTime && actualBreakMinutes > 0 && (
                                          <small className="break-deduction" title={`Pausenzeit abgezogen: ${actualBreakMinutes} Min`}>
                                            <><Clock size={12} /> -{actualBreakMinutes}min</>
                                          </small>
                                        )}
                                        {log.description && (
                                          <small className="session-desc">{log.description}</small>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        const fallbackDate = new Date(year, month - 1, dayNum);
                        const dayOfWeek = fallbackDate.getDay();
                        const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
                        const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
                        const dayLabel = dayNames[normalizedDay - 1];
                        
                        calendarCells.push(
                          <div key={dateStr} className="calendar-day no-data">
                            <div className="day-header">
                              <span className="day-number">{dayNum}</span>
                              <span className="day-name">{dayLabel}</span>
                            </div>
                          </div>
                        );
                      }
                    }
                    
                    return calendarCells;
                  })()
                ) : (
                  data.days.map(day => (
                    <div 
                      key={day.date} 
                      className={`${getDayStatusClass(day)} clickable`}
                      onClick={() => openDayModal(day)}
                      title="Klicken für Details"
                    >

                      <div className="day-header">
                        <span className="day-number">{new Date(day.date).getDate()}</span>
                        <span className="day-name">{getDayLabel(day)}</span>
                      </div>
                      
                      <div className="day-content">
                        {day.actualMinutes > 0 && (
                          <div className="work-time">
                            {formatMinutes(day.actualMinutes)}
                          </div>
                        )}
                        
                        {/* NEW: Show daily overtime/undertime */}
                        {day.expectedMinutes > 0 && (day.actualMinutes !== day.expectedMinutes) && (
                          <div className={`day-overtime ${day.actualMinutes - day.expectedMinutes >= 0 ? 'positive' : 'negative'}`}>
                            {day.actualMinutes - day.expectedMinutes >= 0 ? '+' : ''}{formatMinutes(day.actualMinutes - day.expectedMinutes)}
                          </div>
                        )}
                        
                        {day.holiday && (
                          <div className="day-info holiday">
                            <Gift size={12}/> {day.holiday.name}
                          </div>
                        )}
                        
                        {(() => {
                          const dayTimeOffEntries = getTimeOffEntriesForDay(day.date);
                          
                          if (dayTimeOffEntries.length === 0 && day.timeOff) {
                            return (
                              <div className={`day-info timeoff ${day.timeOff.type.toLowerCase()}`}>
                                {getTimeOffIcon(day.timeOff.type)} {getTimeOffLabel(day.timeOff.type, day.timeOff.reason)}
                              </div>
                            );
                          }

                          return dayTimeOffEntries.map((entry, index) => (
                            <div key={`${entry.id}-${index}`} className={`day-info timeoff ${entry.type.toLowerCase()}`}>
                              {getTimeOffIcon(entry.type)} {getTimeOffLabel(entry.type, entry.reason)}
                            </div>
                          ));
                        })()}
                        
                        {day.workLogs && day.workLogs.length > 0 && (
                          <div className="work-sessions">
                            {day.workLogs.map(log => {
                              // Use actual break minutes from database instead of calculating difference
                              const actualBreakMinutes = getActualBreakMinutes(log);
                              const isAutoClockOut = log.description && log.description.includes('Auto-Ausstempeln');
                              
                              return (
                                <div key={log.id} className="work-session">
                                  <small>
                                    {new Date(log.startTime).toLocaleTimeString('de-DE', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                    {log.endTime && 
                                      ` - ${new Date(log.endTime).toLocaleTimeString('de-DE', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}`
                                    }
                                    {isAutoClockOut && (
                                      <span className="auto-clockout-indicator" title="Automatisch ausgestempelt">
                                        🤖
                                      </span>
                                    )}
                                  </small>
                                  {log.endTime && actualBreakMinutes > 0 && (
                                    <small className="break-deduction" title={`Pausenzeit abgezogen: ${actualBreakMinutes} Min`}>
                                      ⏱️ -{actualBreakMinutes}min
                                    </small>
                                  )}
                                  {<div className="method-badges">
                                    <span className={`method-badge ${(log.logMethod || 'WEBSITE').toLowerCase()}`}>
                                      {(log.logMethod || 'WEBSITE') === 'WEBSITE' ? <Laptop size={12} /> : (log.logMethod || 'WEBSITE') === 'NFC' ? <SmartphoneNfc size={14} /> : <Pencil size={14} />} 
                                      {log.logMethod || 'WEBSITE'}
                                    </span>
                                    {log.endTime && log.logMethod2 && (
                                      <>
                                        <span className="method-arrow">→</span>
                                        <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                                          {log.logMethod2 === 'WEBSITE' ? <Laptop size={12} /> : log.logMethod2 === 'NFC' ? <SmartphoneNfc size={14} /> : <Pencil size={14} />} 
                                          {log.logMethod2}
                                        </span>
                                      </>
                                    )}
                                  </div> }
                                  {log.description && (
                                    <small className="session-desc">{log.description}</small>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Vacation Summary Cards */}
          {vacationSummary && (
            <div className="vacation-summary-section">
              <h3>🏖️ Urlaubsübersicht {vacationSummary.year}</h3>
              <div className="vacation-summary-cards">
                <div className="summary-card vacation-total">
                  <h4>Gesamt Urlaubstage</h4>
                  <p className="big-number">{vacationSummary.totalVacationDays}</p>
                  <small>Für das Jahr {vacationSummary.year}</small>
                </div>
                <div className="summary-card vacation-used">
                  <h4>Verwendet</h4>
                  <p className="big-number">{vacationSummary.usedVacationDays}</p>
                  <small>Genehmigte Urlaubstage</small>
                </div>
                <div className="summary-card vacation-pending">
                  <h4>Wartend</h4>
                  <p className="big-number">{vacationSummary.pendingVacationDays}</p>
                  <small>Noch nicht genehmigt</small>
                </div>
                <div className="summary-card vacation-remaining">
                  <h4>Verbleibend</h4>
                  <p className="big-number">{vacationSummary.remainingVacationDays}</p>
                  <small>Noch verfügbar</small>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Day Detail Modal */}
          {showDayModal && selectedDay && (
            <div className="modal-overlay" onClick={closeDayModal}>
              <div className="modal-content day-modal admin-day-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    {new Date(selectedDay.date).toLocaleDateString('de-DE', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    {data && (
                      <span className="modal-user-info">
                        - {data.user.firstName} {data.user.lastName}
                      </span>
                    )}
                  </h3>
                  <button className="modal-close" onClick={closeDayModal}>×</button>
                </div>
                
                <div className="modal-body">
                  <div className="day-status-info">
                    <span className={`status-badge ${selectedDay.status}`}>
                      {selectedDay.status === 'workday' && <><Briefcase size={12} /> Arbeitstag</>} 
                      {selectedDay.status === 'weekend' && <><Home size={12} />Wochenende</>} 
                      {selectedDay.status === 'holiday' && <><Gift size={12} />Feiertag</>} 
                      {selectedDay.status === 'vacation' && <><Palmtree size={12} />Urlaub</>} 
                      {selectedDay.status === 'sick' && <><Heart size={12} />Kranktag</>} 
                      {selectedDay.status === 'current' && <><Calendar size={12} />Heute</>} 
                      {selectedDay.status === 'overtime' && <><Clock size={12} />Überstunden</>} 
                    </span>
                  </div>

                  {selectedDay.holiday && (
                    <div className="info-section holiday-info">
                      <h4><Gift size={12} /> Feiertag</h4>
                      <p><strong>{selectedDay.holiday.name}</strong></p>
                      {selectedDay.holiday.description && <p>{selectedDay.holiday.description}</p>}
                    </div>
                  )}

                  {(() => {
                    const dayTimeOffEntries = getTimeOffEntriesForDay(selectedDay.date);
                    if (dayTimeOffEntries.length === 0 && selectedDay.timeOff) {
                      return (
                        <div className="info-section timeoff-info">
                          <div className="timeoff-header">
                            <h4>
                              {getTimeOffIcon(selectedDay.timeOff.type)} {getTimeOffLabel(selectedDay.timeOff.type)}
                              {canEditTimes() && (
                                <button
                                  className="btn-icon delete"
                                  onClick={() => {
                                    const timeOffEntry = getTimeOffEntriesForDay(selectedDay.date)[0];
                                    if (timeOffEntry) {
                                      handleDeleteTimeOff(timeOffEntry.id, timeOffEntry.type);
                                    }
                                  }}
                                  title="Urlaub/Krankmeldung löschen"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </h4>
                          </div>
                          {selectedDay.timeOff.reason && <p>{selectedDay.timeOff.reason}</p>}
                        </div>
                      );
                    }
                    
                    return dayTimeOffEntries.map((entry, index) => (
                      <div key={`${entry.id}-${index}`} className="info-section timeoff-info">
                        <div className="timeoff-header">
                          <h4>
                            {getTimeOffIcon(entry.type)} {getTimeOffLabel(entry.type)}
                            {canEditTimes() && (
                              <button
                                className="btn-icon delete"
                                onClick={() => handleDeleteTimeOff(entry.id, entry.type)}
                                title={`${getTimeOffLabel(entry.type)} löschen`}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </h4>
                        </div>
                        {entry.reason && <p>{entry.reason}</p>}
                        <div className="timeoff-details">
                          <small>
                            {new Date(entry.startDate).toLocaleDateString('de-DE')} - {new Date(entry.endDate).toLocaleDateString('de-DE')}
                          </small>
                        </div>
                      </div>
                    ));
                  })()}

                  <div className="time-summary">
                    <div className="time-info">
                      <h4><Clock size={16} /> Zeiten</h4>
                      <div className="time-details">
                        <div className="time-row">
                          <span>Arbeitszeit:</span>
                          <span className="time-value">{formatMinutes(selectedDay.actualMinutes)}</span>
                        </div>
                        <div className="time-row">
                          <span>Soll-Zeit:</span>
                          <span className="time-value">{formatMinutes(selectedDay.expectedMinutes)}</span>
                        </div>
                        <div className="time-row">
                          <span>Differenz:</span>
                          <span className={`time-value ${selectedDay.actualMinutes - selectedDay.expectedMinutes >= 0 ? 'positive' : 'negative'}`}>
                            {formatMinutes(selectedDay.actualMinutes - selectedDay.expectedMinutes)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedDay.workLogs && selectedDay.workLogs.length > 0 && (
                    <div className="info-section work-logs-detail">
                      <h4><BarChart3 size={16} /> Arbeitszeiten im Detail</h4>
                      <div className="work-logs-list">
                        {selectedDay.workLogs.map(log => {
                          // Use actual break minutes from database instead of calculating difference
                          const loggedMinutes = log.endTime 
                            ? Math.round((new Date(log.endTime).getTime() - new Date(log.startTime).getTime()) / (1000 * 60))
                            : 0;
                          const actualMinutes = log.totalMinutes || 0;
                          const actualBreakMinutes = getActualBreakMinutes(log);
                          const isAutoClockOut = log.description && log.description.includes('Auto-Ausstempeln');
                          
                          return (
                            <div 
                              key={log.id} 
                              className="work-log-item clickable"
                              onClick={() => canEditTimes() && handleEditWorkLog(log.id)}
                              style={{ cursor: canEditTimes() ? 'pointer' : 'default' }}
                              title={canEditTimes() ? "Klicken zum Bearbeiten" : ""}
                            >
                              <div className="log-time">
                                <strong>
                                  {new Date(log.startTime).toLocaleTimeString('de-DE', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })} Uhr
                                  {log.endTime && 
                                    ` - ${new Date(log.endTime).toLocaleTimeString('de-DE', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })} Uhr`
                                  }
                                  {isAutoClockOut && (
                                    <span className="auto-clockout-indicator" title="Automatisch ausgestempelt nach Erreichen der maximalen Arbeitszeit">
                                      <Bot size={12} /> Auto
                                    </span>
                                  )}
                                </strong>
                                {canEditTimes() && (
                                  <button
                                    className="btn-icon edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditWorkLog(log.id);
                                    }}
                                    title="Bearbeiten"
                                  >
                                    <Edit size={16} />
                                  </button>
                                )}
                              </div>
                              
                              <div className="log-details">
                                {log.endTime && (
                                  <>
                                    <div className="log-calculation">
                                      <span>Geloggte Zeit: {formatMinutes(loggedMinutes)}</span>
                                      {actualBreakMinutes > 0 && (
                                        <span className="break-info">
                                          Abzug Pausenzeit: -{formatMinutes(actualBreakMinutes)}
                                        </span>
                                      )}
                                      <span className="actual-time">
                                        <strong>Arbeitszeit: {formatMinutes(actualMinutes)}</strong>
                                      </span>
                                    </div>
                                  </>
                                )}
                                
                                {!log.endTime && (
                                  <div className="log-status">
                                    <span className="active-session"><Circle size={12} fill='green' color="green" /> Aktive Sitzung</span>
                                  </div>
                                )}
                                
                                <div className="log-methods">
                                  <div className="method-badges">
                                    <span className={`method-badge ${(log.logMethod || 'WEBSITE').toLowerCase()}`}>
                                      Ein: {(log.logMethod || 'WEBSITE') === 'WEBSITE' ? <Laptop size={12} /> : (log.logMethod || 'WEBSITE') === 'NFC' ? <SmartphoneNfc size={14} /> : <Pencil size={14} />} 
                                      {log.logMethod || 'WEBSITE'}
                                    </span>
                                    {log.endTime && log.logMethod2 && (
                                      <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                                        Aus: {log.logMethod2 === 'WEBSITE' ? <Laptop size={12} /> : log.logMethod2 === 'NFC' ? <SmartphoneNfc size={14} /> : <Pencil size={14} />} 
                                        {log.logMethod2}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {log.description && (
                                  <div className="log-description">
                                    <span>Beschreibung: {log.description}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedDay.workLogs.length === 0 && selectedDay.expectedMinutes > 0 && (
                    <div className="info-section no-work">
                      <p><Info size={12} /> Keine Arbeitszeiten erfasst</p>
                    </div>
                  )}

                  {/* Admin Actions */}
                  {canEditTimes() && (
                    <div className="admin-actions">
                      <h4><Wrench size={16} /> Admin-Aktionen</h4>
                      <div className="action-buttons">
                        <button 
                          className="btn primary"
                          onClick={() => handleAddWorkLog(selectedDay.date)}
                        >
                          <Plus size={12} /> Arbeitszeit hinzufügen
                        </button>
                        <button 
                          className="btn secondary"
                          onClick={() => {
                            setVacationForm({
                              startDate: selectedDay.date,
                              endDate: selectedDay.date,
                              reason: '',
                              type: 'VACATION'
                            });
                            setShowVacationForm(true);
                            closeDayModal();
                          }}
                        >
                          <Palmtree size={12} /> Urlaub eintragen
                        </button>
                        {canEditTimes() && (
                          <button 
                            className="btn secondary"
                            onClick={() => {
                              setSickForm({
                                startDate: selectedDay.date,
                                endDate: selectedDay.date,
                                reason: '',
                                doctorNote: false
                              });
                              setShowSickForm(true);
                              closeDayModal();
                            }}
                          >
                            <Heart size={12} /> Krankmeldung eintragen
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Vacation Form Modal */}
          {showVacationForm && data && (
            <div className="modal-overlay" onClick={() => setShowVacationForm(false)}>
              <div className="modal-content vacation-form-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3><Palmtree /> Urlaub eintragen für {data.user.firstName} {data.user.lastName}</h3>
                  <button className="modal-close" onClick={() => setShowVacationForm(false)}>×</button>
                </div>
                
                <div className="modal-body">
                  {vacationSummary && (
                    <div className="vacation-info-box">
                      <p><strong>Verfügbare Urlaubstage:</strong> {vacationSummary.remainingVacationDays} von {vacationSummary.totalVacationDays}</p>
                    </div>
                  )}

                  <form onSubmit={handleCreateVacation}>
                    <div className="form-group">
                      <label>Urlaubstyp:</label>
                      <select
                        value={vacationForm.type}
                        onChange={(e) => setVacationForm(prev => ({ ...prev, type: e.target.value as 'VACATION' | 'SPECIAL_LEAVE' }))}
                        required
                      >
                        <option value="VACATION"><Palmtree size={12} /> Normaler Urlaub</option>
                        <option value="SPECIAL_LEAVE"><Target size={12} /> Sonderurlaub</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Startdatum:</label>
                        <input
                          type="date"
                          value={vacationForm.startDate}
                          onChange={(e) => setVacationForm(prev => ({ ...prev, startDate: e.target.value }))}
                          className={conflictDates.includes(vacationForm.startDate) ? 'conflict-date' : ''}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Enddatum:</label>
                        <input
                          type="date"
                          value={vacationForm.endDate}
                          onChange={(e) => setVacationForm(prev => ({ ...prev, endDate: e.target.value }))}
                          min={vacationForm.startDate}
                          className={conflictDates.includes(vacationForm.endDate) ? 'conflict-date' : ''}
                          required
                        />
                      </div>
                    </div>

                    {conflictDates.length > 0 && (
                      <div className="conflict-warning">
                        <p>⚠️ Konflikt: {conflictDates.length} Tag(e) bereits als Urlaub genehmigt!</p>
                        <div className="conflict-dates">
                          {conflictDates.map(date => (
                            <span key={date} className="conflict-date-tag">
                              {new Date(date).toLocaleDateString('de-DE')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {calculatedWorkingDays > 0 && (
                      <div className="calculated-days">
                        <p><strong>Berechnete Arbeitstage:</strong> {calculatedWorkingDays} Tage</p>
                        {vacationSummary && calculatedWorkingDays > vacationSummary.remainingVacationDays && vacationForm.type === 'VACATION' && (
                          <p className="warning">Nicht genügend Urlaubstage verfügbar!</p>
                        )}
                      </div>
                    )}

                    <div className="form-group">
                      <label>Grund (optional):</label>
                      <textarea
                        value={vacationForm.reason}
                        onChange={(e) => setVacationForm(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder="Grund für den Urlaub..."
                        rows={3}
                      />
                    </div>

                    <div className="form-actions">
                      <button type="button" className="btn secondary" onClick={() => setShowVacationForm(false)}>
                        Abbrechen
                      </button>
                      <button type="submit" className="btn primary" disabled={loading || conflictDates.length > 0}>
                        {loading ? 'Wird eingetragen...' : `${vacationForm.type === 'SPECIAL_LEAVE' ? 'Sonderurlaub' : 'Urlaub'} eintragen`}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Sick Leave Form Modal */}
          {showSickForm && canEditTimes() && data && (
            <div className="modal-overlay" onClick={() => setShowSickForm(false)}>
              <div className="modal-content sick-form-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3><Heart /> Krankmeldung eintragen für {data.user.firstName} {data.user.lastName}</h3>
                  <button className="modal-close" onClick={() => setShowSickForm(false)}>×</button>
                </div>
                
                <div className="modal-body">
                  <form onSubmit={handleCreateSickLeave}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Startdatum:</label>
                        <input
                          type="date"
                          value={sickForm.startDate}
                          onChange={(e) => setSickForm(prev => ({ ...prev, startDate: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Enddatum:</label>
                        <input
                          type="date"
                          value={sickForm.endDate}
                          onChange={(e) => setSickForm(prev => ({ ...prev, endDate: e.target.value }))}
                          min={sickForm.startDate}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Grund/Diagnose (optional):</label>
                      <textarea
                        value={sickForm.reason}
                        onChange={(e) => setSickForm(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder="Grund der Krankheit..."
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={sickForm.doctorNote}
                          onChange={(e) => setSickForm(prev => ({ ...prev, doctorNote: e.target.checked }))}
                        />
                        Ärztliches Attest vorhanden
                      </label>
                    </div>

                    <div className="form-actions">
                      <button type="button" className="btn secondary" onClick={() => setShowSickForm(false)}>
                        Abbrechen
                      </button>
                      <button type="submit" className="btn primary" disabled={loading}>
                        {loading ? 'Wird eingetragen...' : 'Krankmeldung eintragen'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* WorkLog Edit Modal */}
          {showWorkLogModal && data && selectedUserId && (
            <WorkLogEditModal
              isOpen={showWorkLogModal}
              onClose={closeWorkLogModal}
              onSuccess={handleWorkLogSuccess}
              workLog={editingWorkLog}
              userId={selectedUserId}
              date={editingDate}
              userName={`${data.user.firstName} ${data.user.lastName}`}
            />
          )}
        </>
      )}

      {!selectedUserId && (
        <div className="no-user-selected">
          <h3><User /> Mitarbeiter auswählen</h3>
          <p>Bitte wählen Sie einen Mitarbeiter aus, um dessen Monatsübersicht anzuzeigen.</p>
        </div>
      )}
    </div>
  );
};